/**
 * Role + permission utilities for Thaklis Managers.
 *
 * Structural roles (the `role` string on the session user):
 * - PLATFORM_ADMIN / PLATFORM_SUPPORT: platform level (all typing centers)
 * - TC_ADMIN: full access to own typing center
 * - TC_STAFF: access driven by the per-TC custom role's permission matrix
 * - GROUP_OWNER: multi-TC owner (acts as TC_ADMIN once inside a TC)
 *
 * NOTE: the `is*(role)` helpers take the role STRING; `can(user, ...)` takes the
 * whole USER object (it reads the resolved `permissions` matrix attached at login).
 */

// ── Structural role checks (take the role string) ────────────────────────────
export const isPlatformLevel = (role) => ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT'].includes(role);
export const isPlatformAdmin = (role) => role === 'PLATFORM_ADMIN';
export const isPlatformSupport = (role) => role === 'PLATFORM_SUPPORT';
export const isTcAdmin = (role) => role === 'TC_ADMIN';
export const isGroupOwner = (role) => role === 'GROUP_OWNER';

// PLATFORM_SUPPORT is view-only; everyone else may write (subject to the matrix for TC_STAFF).
export const hasWriteAccess = (role) => role !== 'PLATFORM_SUPPORT';

// ── Matrix permission check (takes the user object) ──────────────────────────
// Reads the resolved matrix ({feature: {read, write, delete}}) that the backend
// attaches to the session user at login / TC-switch. Missing feature/action ⇒ false.
// Sessions stored before the matrix existed have no `permissions` at all; for those,
// fall back to the structural role (mirrors PermissionMatrix.forUser on the backend)
// so an already-logged-in admin isn't left with an empty sidebar until re-login.
export const can = (user, feature, action) => {
  if (!user) return false;
  if (user.permissions == null || Object.keys(user.permissions).length === 0) {
    if (user.role === 'PLATFORM_ADMIN' || user.role === 'TC_ADMIN') return true;
    if (user.role === 'PLATFORM_SUPPORT') return action === 'read';
    return false;
  }
  return user.permissions[feature]?.[action] === true;
};

// A commission agent with no operational permission at all lands on My Commissions instead of the dashboard.
export const isAgentOnly = (user) => user?.isCommissionRecipient === true && user.role === 'TC_STAFF'
  && !Object.values(user.permissions || {}).some((p) => p?.read === true);

// ── Thaklis-features / B2B toggles (take the user object) ─────────────────────
// Disabled only when explicitly false (backward-compatible with TCs where it's absent).
export const isThaklisDisabled = (user) => user?.thaklisEnabled === false;
export const isB2bTc = (user) => user?.organizationType === 'B2B';

// A promoted TC (a B2B's customer TC that was promoted into a real TC).
export const isPromotedTc = (user) => user?.isPromoted === true;

// Gate for the client-side B2B connection features + cap: promoted TCs always, plus independent TCs
// a platform admin has granted the feature. Backend sends `connectionsEnabled` (true for both cases).
export const canUseConnections = (user) => user?.isPromoted === true || user?.connectionsEnabled === true;

// ── Presentation ─────────────────────────────────────────────────────────────
export const getRoleLabel = (role) => {
  switch (role) {
    case 'PLATFORM_ADMIN': return 'Platform Admin';
    case 'PLATFORM_SUPPORT': return 'Support';
    case 'TC_ADMIN': return 'Admin';
    case 'TC_STAFF': return 'Staff';
    case 'GROUP_OWNER': return 'Owner';
    default: return role;
  }
};

export const getRoleChipColor = (role) => {
  switch (role) {
    case 'PLATFORM_ADMIN': return 'error';
    case 'PLATFORM_SUPPORT': return 'warning';
    case 'TC_ADMIN': return 'primary';
    case 'TC_STAFF': return 'info';
    case 'GROUP_OWNER': return 'secondary';
    default: return 'default';
  }
};
